// Forhåndsvisning av pristilbudet i EditOffer: det kunden ser på lenken, bygget av det som står i
// skjemaet nå (også endringer som ikke er lagret). Bruker samme visning som kundesiden
// (pages/tilbud/TilbudVisning.jsx), så forhåndsvisningen og kundesiden ikke kan skli fra hverandre.
// Dialog etter U2 (useDialog): fokus inn, Tab holdes inne, Esc lukker, fokus tilbake.
import React from 'react';
import { useDialog } from '../components/tilgjengelighet';
import { TilbudTopp, TilbudOversikt } from '../pages/tilbud/TilbudVisning';
import { knappSekundar } from '../ny-kundesamhandling/Skjema';
import { linjeTall, oppsummering } from './kalkyle';

const tall = (v) => Number(String(v ?? '').replace(',', '.')) || 0;

// Linjene slik serveren lagrer dem (src/lib/prislinjer.js): antall 1–999, rabatt i %, total.
export function somKundenSer(linjer) {
  return linjer.map((l) => ({
    artikkel: String(l.artikkel || '').trim() || 'Vare',
    antall: Math.min(999, Math.max(1, Math.round(tall(l.antall) || 1))),
    pris: tall(l.pris),
    rabatt: tall(l.rabatt),
    totalRabattKr: linjeTall(l).rabattKr,
    totalprisEtterRabatt: linjeTall(l).total,
  }));
}

// klubbNavn: pristilbud til en klubb (ordre/trykk fra klubbinfo); ellers fra tilbudet (EditOffer).
export default function ForhandsvisTilbud({ apen, onLukk, offer, skjema, fornavn, klubbNavn = '' }) {
  const dialogRef = useDialog(apen, onLukk);
  if (!apen) return null;

  const visning = {
    offerRef: offer?.offerRef,
    klubbNavn: klubbNavn || offer?.kontaktKlubb?.klubbNavn || '',
    registrertDato: offer?.registrertDato || new Date(),
    utloper: skjema.utloper ? new Date(`${skjema.utloper}T12:00:00`) : offer?.utloper,
    produkter: somKundenSer(skjema.linjer),
    kommentar: skjema.kommentar,
    rabattVisning: skjema.rabattVisning,
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-2 sm:p-5">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="fv-tittel" aria-describedby="fv-forklaring"
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-2 border-b border-slate-200 px-3 py-1.5 sm:px-3">
          <div className="min-w-0 flex-1">
            <h2 id="fv-tittel" className="text-base font-bold text-slate-900">Forhåndsvisning</h2>
            <p id="fv-forklaring" className="text-sm text-slate-700">
              Slik ser kunden tilbudet på lenken. Viser det som står i skjemaet nå, også endringer som ikke er lagret.
            </p>
          </div>
          <button type="button" onClick={onLukk} className={`${knappSekundar} shrink-0`} data-autofokus>Lukk</button>
        </div>

        {/* Kundesiden har grå bakgrunn rundt innholdet; samme visning her. */}
        <div className="space-y-3 bg-gray-50 px-3 py-5 sm:px-6">
          <TilbudTopp
            offer={visning}
            overskrift="h3"
            merke={<span className="shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-blue-50 text-blue-700">Til godkjenning</span>}
          />
          <p className="text-gray-700 text-sm">Hei{fornavn ? ` ${fornavn}` : ''},</p>
          <TilbudOversikt offer={visning} totalPris={oppsummering(skjema.linjer).total} overskrift="h4" />
          <div className="rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 p-3 text-sm text-blue-900">
            <p className="font-semibold">Godkjenn tilbudet</p>
            <p>Her får kunden knappene «Godkjenn» og «Avslå», kan skrive en kommentar og sende deg meldinger.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
